import { CompletionStatus } from '@completion/enums';

export interface ProcurementPackageAssignmentLike {
  id?: number | null;
  procurementPackageId?: number | null;
  procurementPackageNumber?: string | null;
  systemNumber?: string | null;
  tagNumber?: string | null;
  checkSheetNumber?: string | null;
  progress?: number | null;
  status?: string | null;
  state?: string | null;
}

export interface ProcurementPackageGroup<T extends ProcurementPackageAssignmentLike> {
  packageKey: string;
  packageId: number | null;
  packageNumber: string;
  assignments: T[];
  expanded: boolean;
}

export interface PackageFocusTarget {
  id: number;
  packageKey: string;
}

export function normalizeCompletionStatus(value: string | null | undefined): CompletionStatus {
  if (value == null) {
    return CompletionStatus.OS;
  }

  switch (String(value).trim().toUpperCase()) {
    case 'OS':
    case 'OPEN':
      return CompletionStatus.OS;
    case 'OK':
    case 'COMPLETED':
    case 'COMPLETE':
      return CompletionStatus.OK;
    case 'NA':
    case 'N/A':
      return CompletionStatus.NA;
    case 'PA':
      return CompletionStatus.PA;
    case 'PB':
      return CompletionStatus.PB;
    default:
      return CompletionStatus.OS;
  }
}

export function getProcurementPackageId<T extends ProcurementPackageAssignmentLike>(assignment: T): number | null {
  const packageId = assignment.procurementPackageId;
  return typeof packageId === 'number' ? packageId : null;
}

export function getProcurementPackageNumber<T extends ProcurementPackageAssignmentLike>(assignment: T): string {
  const packageNumber = assignment.procurementPackageNumber;
  const normalized = typeof packageNumber === 'string' ? packageNumber.trim() : '';
  return normalized || 'Unknown package';
}

export function getProcurementPackageKey(packageId: number | null, packageNumber: string): string {
  return packageId != null ? `id:${packageId}` : `num:${packageNumber.toLowerCase()}`;
}

export function compareScopeText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

export function compareNullableNumber(a: number | null, b: number | null): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  return a - b;
}

export function compareAssignmentsByScopeFields<T extends ProcurementPackageAssignmentLike>(a: T, b: T): number {
  return (
    compareScopeText((a.systemNumber || '').toString(), (b.systemNumber || '').toString()) ||
    compareScopeText(a.tagNumber || '', b.tagNumber || '') ||
    compareScopeText(a.checkSheetNumber || '', b.checkSheetNumber || '')
  );
}

export function buildProcurementPackageGroups<T extends ProcurementPackageAssignmentLike>(
  assignments: T[],
  expandedMap: Map<string, boolean>,
): ProcurementPackageGroup<T>[] {
  const byPackage = new Map<
    string,
    {
      packageId: number | null;
      packageNumber: string;
      assignments: T[];
    }
  >();

  for (const assignment of assignments || []) {
    const packageId = getProcurementPackageId(assignment);
    const packageNumber = getProcurementPackageNumber(assignment);
    const packageKey = getProcurementPackageKey(packageId, packageNumber);
    const existing = byPackage.get(packageKey);

    if (existing) {
      existing.assignments.push(assignment);
      continue;
    }

    byPackage.set(packageKey, {
      packageId,
      packageNumber,
      assignments: [assignment],
    });
  }

  return Array.from(byPackage.entries())
    .map(([packageKey, value]) => ({
      packageKey,
      packageId: value.packageId,
      packageNumber: value.packageNumber,
      assignments: [...value.assignments].sort((a, b) => compareAssignmentsByScopeFields(a, b)),
      expanded: expandedMap.get(packageKey) ?? false,
    }))
    .sort((a, b) => {
      const byNumber = compareScopeText(a.packageNumber, b.packageNumber);
      if (byNumber !== 0) {
        return byNumber;
      }
      return compareNullableNumber(a.packageId, b.packageId);
    });
}

export function findGroupAssignmentByTag<T extends ProcurementPackageAssignmentLike>(
  groups: ProcurementPackageGroup<T>[],
  query: string,
): { group: ProcurementPackageGroup<T>; assignment: T } | null {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return null;
  }

  for (const group of groups || []) {
    const hit = (group.assignments || []).find((assignment) => (assignment.tagNumber || '').toLowerCase().includes(normalizedQuery));
    if (hit) {
      return { group, assignment: hit };
    }
  }

  return null;
}

export function getGroupProgressPercent<T extends { progress?: number | null }>(group: { assignments: T[] }): number {
  const assignments = group?.assignments || [];
  if (assignments.length === 0) {
    return 0;
  }

  const sum = assignments.reduce((acc, assignment) => acc + (typeof assignment.progress === 'number' ? assignment.progress : 0), 0);
  return Math.round((sum / assignments.length) * 100);
}

export function getAssignmentProgressPercent<T extends { progress?: number | null }>(assignment: T): number {
  const value = (assignment?.progress ?? 0) * 100;
  return Math.max(0, Math.min(100, value));
}

export function detectPackageFocusChange<T extends ProcurementPackageAssignmentLike>(
  prevGroups: ProcurementPackageGroup<T>[],
  nextGroups: ProcurementPackageGroup<T>[],
  getStatus: (assignment: T) => CompletionStatus,
): PackageFocusTarget | null {
  if (!Array.isArray(prevGroups) || !Array.isArray(nextGroups)) {
    return null;
  }

  const prevMap = new Map<number, { progress: number; packageKey: string; status: CompletionStatus }>();
  for (const group of prevGroups) {
    for (const assignment of group.assignments || []) {
      if (typeof assignment?.id !== 'number') {
        continue;
      }

      prevMap.set(assignment.id, {
        progress: assignment.progress ?? 0,
        packageKey: group.packageKey,
        status: getStatus(assignment),
      });
    }
  }

  let bestProgressDelta: { id: number; packageKey: string; delta: number } | null = null;
  let firstStatusChange: PackageFocusTarget | null = null;

  for (const group of nextGroups) {
    for (const assignment of group.assignments || []) {
      const id = assignment?.id as number;
      if (typeof id !== 'number') {
        continue;
      }

      const prev = prevMap.get(id);
      if (!prev) {
        continue;
      }

      const currentStatus = getStatus(assignment);
      if (!firstStatusChange && prev.status !== currentStatus) {
        firstStatusChange = { id, packageKey: group.packageKey };
      }

      const delta = (assignment.progress ?? 0) - (prev.progress ?? 0);
      if (delta > 0.0001 && (!bestProgressDelta || delta > bestProgressDelta.delta)) {
        bestProgressDelta = { id, packageKey: group.packageKey, delta };
      }
    }
  }

  return firstStatusChange ?? (bestProgressDelta ? { id: bestProgressDelta.id, packageKey: bestProgressDelta.packageKey } : null);
}
